/**
 * @fileoverview Hook Data Layer to load and manage customer notes in Inbox.
 * @module useCustomerNotesData
 * @category Hook - Data Layer
 * @since 3.1.0
 * @version 3.1.0
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import customersModelMc from "../../../models/CustomersModelMc";
import { LOAD_DATA_FAILURE } from "../../../../actions/inbox/conversationActions";
import useSelectedConversation from "../../shared/useSelectedConversation";
import { sharingInformationService } from "../../../../services/sharingInformationService";
import { isInboxMsChat } from "../../../../services/inbox/inboxMsChatPlatform";

/**
 * Hook Data Layer specialized to load customer notes in Inbox.
 *
 * - Loads notes associated with the customer of the selected conversation.
 * - Exposes loading, data and reload function.
 * - Formats the date according to the current language.
 * - Optimized with useCallback and useMemo.
 *
 * @function useCustomerNotesData
 * @param {string} platform - Conversation platform (whatsapp, messenger, instagram)
 * @param {string} language - Current language for date formatting
 * @returns {{ customerNotes: Array<Object>, loading: boolean, reload: function }}
 *
 * @see CustomerNotesMC Presentation component
 * @since 3.1.0
 * @version 3.1.0
 */
export default function useCustomerNotesData(platform, language) {
  const dispatch = useDispatch();
  const [customerNotes, setCustomerNotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const conversation = useSelectedConversation(platform);

  // Memoizes the query parameters
  const params = useMemo(() => {
    const platformChats = conversation.isComment ? conversation.platform : `${conversation.platform}_chats`;
    const { customerId } = conversation;
    const customerFbPostId = conversation.customerId !== conversation.id ? conversation.id : 0;
    return { customerId, platformChats, customerFbPostId };
  }, [conversation]);

  /**
   * Loads the customer notes from the API and formats them.
   * @memberof useCustomerNotesData
   */
  const loadNotes = useCallback(async () => {
    setLoading(true);
    try {
      const data = isInboxMsChat(conversation?.platform)
        ? await customersModelMc.getMicroserviceCustomerNotes({
          chatId: conversation.id,
          platform: conversation.platform
        })
        : await customersModelMc.getCustomerNotes({
          customerId: params.customerId,
          platform: params.platformChats,
          customer_fb_post_id: params.customerFbPostId
        });
      // Formats the date of each note according to the language
      const notes = (data.notes || []).map((note) => ({
        ...note,
        formattedDate: moment(note.created_at)
          .local()
          .locale(language)
          .format("DD-MM-YYYY HH:mm")
      }));
      setCustomerNotes(notes);
    } catch (error) {
      dispatch({
        type: LOAD_DATA_FAILURE,
        error: error.message || 'Error loading customer notes.'
      });
    } finally {
      setLoading(false);
    }
  }, [conversation?.id, conversation?.platform, params.customerId, params.platformChats, params.customerFbPostId, language, dispatch]);

  // Automatic load when changing conversation
  useEffect(() => {
    if (params.customerId) {
      loadNotes();
    }
  }, [params.customerId, params.platformChats, params.customerFbPostId, loadNotes]);

  useEffect(() => {
    const subscription$ = sharingInformationService.getSubject();

    const myObservable = subscription$.subscribe((data) => {
      if (data.refreshListDataNote) {
        loadNotes();
      }
    });

    return () => {
      myObservable.unsubscribe();
    };
  }, [loadNotes]);

  // Exposes the API of the hook
  return useMemo(() => ({
    customerNotes,
    loading,
    reload: loadNotes
  }), [customerNotes, loading, loadNotes]);
}
